/**
 * 服务管理业务逻辑整合 Hook
 * 
 * 职责：
 * 1. 整合服务管理相关的状态和逻辑
 * 2. 提供统一的业务接口给页面组件
 * 3. 协调多个子 Hook 的工作
 * 
 * 注意：这不是页面组件，而是业务逻辑的整合层
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { message } from "antd";

// 导入配置和工具
import { DEFAULT_SERVICE_STATE, ServiceManagementState } from "./useServiceState";
import {
  calculateServiceStats,
  processSearchParams,
  processPaginationParams,
  resetSearchParams,
  resetPaginationParams,
  handlePaginationChange,
  validateHealthCheckUrl,
  getTokenFormInitialValues,
  getEditFormInitialValues,
  handleTokenOperationResult,
  handleServiceOperationResult,
  createLoadingMessageConfig,
  handleHealthCheckResult,
} from "../utils/serviceManagementUtils";

// 导入现有的 Hooks
import { useServiceManagement } from "./useServiceManagement";
import { useServiceToken } from "./useServiceToken";
import { useServiceOperations } from "./useServiceOperations";
import { PermissionService } from "../service-apis/permissionService";

// 导入配置
import { TOKEN_CONFIG } from "../config/modalTokenConfig";
import { unifiedTokenFormFields, createTokenModalFooter } from "./useTokenFormConfig";

export const useServiceManagementLogic = () => {

  // 业务状态管理
  const [state, setState] = useState<ServiceManagementState>(DEFAULT_SERVICE_STATE);
  const [tokenFormRef, setTokenFormRef] = useState<any>(null);

  // 防止权限计算无限循环的ref
  const permissionCalculationRef = useRef(false);
  // 权限数据缓存
  const permissionsCacheRef = useRef<Map<string, number>>(new Map());
  // 防抖定时器
  const permissionCalculationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 计算服务权限数量的函数
  const calculateServicePermissions = useCallback(async (services: any[]) => {
    // 首先尝试从缓存获取权限数据
    const servicesWithPermissions = services.map((service) => {
      const serviceName = service.name || service.service_name || service.key;
      const cachedCount = permissionsCacheRef.current.get(serviceName);

      if (cachedCount !== undefined) {
        return {
          ...service,
          actual_permissions_count: cachedCount,
        };
      }

      return service;
    });

    // 只对没有缓存的服务进行API调用
    const servicesNeedingCalculation = servicesWithPermissions.filter((service) => {
      const serviceName = service.name || service.service_name || service.key;
      if (!serviceName) {
        return false;
      }
      return !permissionsCacheRef.current.has(serviceName);
    });

    if (servicesNeedingCalculation.length > 0) {
      const calculatedServices = await Promise.all(
        servicesNeedingCalculation.map(async (service) => {
          try {
            // 使用正确的字段名获取服务名称
            const serviceName = service.name || service.service_name || service.key;
            console.log(`🔍 计算服务权限数量:`, {
              serviceId: service.id,
              serviceName,
              name: service.name,
              service_name: service.service_name,
              key: service.key,
            });
            const permissions = await PermissionService.fetchServicePermissions(serviceName);
            const count = permissions.length;
            console.log(`✅ 服务 ${serviceName} 有 ${count} 个权限操作:`, permissions.map(p => p.code));

            // 缓存权限数量
            permissionsCacheRef.current.set(serviceName, count);

            return {
              ...service,
              actual_permissions_count: count,
            };
          } catch (error) {
            const serviceName = service.name || service.service_name || service.key;
            console.warn(`❌ 获取服务 ${serviceName} 权限数量失败:`, error);
            // 如果获取失败，设置为 0 并缓存，避免重复请求
            const count = 0;
            permissionsCacheRef.current.set(serviceName, count);
            return {
              ...service,
              actual_permissions_count: count,
            };
          }
        })
      );

      // 更新需要计算的服务
      const result = servicesWithPermissions.map(service => {
        const calculated = calculatedServices.find(calc => calc.id === service.id);
        return calculated || service;
      });

      return result;
    }

    return servicesWithPermissions;
  }, []);


  // 解构状态
  const {
    searchParams,
    pagination,
    createModalVisible,
    editModalVisible,
    tokenModalVisible,
    permissionViewVisible,
    currentRecord,
    serviceStats,
  } = state;

  // 服务管理 Hook
  const {
    serviceList,
    loading,
    total,
    submitting,
    fetchServiceList,
    createService,
    updateService,
    toggleServiceStatus,
    deleteService,
    healthCheck,
    fetchServiceDetail,
    setServiceList,
  } = useServiceManagement();

  // Token管理 Hook
  const {
    tokenInfo,
    submitting: tokenSubmitting,
    createToken,
    updateToken,
    refreshToken,
    deleteToken,
    copyToken,
  } = useServiceToken({
    serviceId: currentRecord?.id,
    serviceName: currentRecord?.service_name,
    visible: tokenModalVisible,
    onTokenChange: () => handleSearch(), // 传递刷新函数
  });

  // 服务操作 Hook
  const {
    handleServiceCreate: handleCreateWithLogic,
    handleServiceEdit: handleEditWithLogic,
    handleServiceStatusToggle: handleToggleStatusWithLogic,
    handleServiceDelete: handleDeleteWithLogic,
    handleHealthCheck: handleHealthCheckWithLogic,
  } = useServiceOperations();

  // 更新状态
  const updateState = useCallback((updates: Partial<ServiceManagementState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // 加载统计信息
  const loadServiceStats = useCallback(() => {
    const stats = calculateServiceStats(serviceList);
    updateState({ serviceStats: stats });
  }, [serviceList, updateState]);

  // 搜索处理
  const handleSearch = useCallback(
    async (params: any = {}) => {
      // 调试信息已移除
      const searchData = processSearchParams(searchParams, params);
      updateState({ searchParams: searchData });

      const paginationData = processPaginationParams(pagination, searchData);
      // 调试信息已移除
      await fetchServiceList(paginationData);
    },
    [searchParams, pagination, fetchServiceList, updateState]
  );

  // 重置搜索
  const handleReset = useCallback(() => {
    // 调试信息已移除
    const resetSearch = resetSearchParams();
    const resetPagination = resetPaginationParams();
    updateState({
      searchParams: resetSearch,
      pagination: resetPagination,
    });
    // 调试信息已移除
    fetchServiceList({ current: 1, pageSize: 10 });
  }, [fetchServiceList, updateState]);

  // 分页处理
  const handlePaginationChangeCallback = useCallback((paginationInfo: any) => {
    const newPagination = {
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    };
    updateState({ pagination: newPagination });

    const paginationData = handlePaginationChange(paginationInfo, searchParams);
    fetchServiceList(paginationData);
  }, [searchParams, fetchServiceList, updateState]);

  // 创建微服务
  const handleCreate = useCallback(async (values: any) => {
    const success = await handleCreateWithLogic(values, () => {
      updateState({ createModalVisible: false });
      handleSearch();
      loadServiceStats();
    });
    return success;
  }, [handleCreateWithLogic, updateState, handleSearch, loadServiceStats]);

  // 编辑微服务
  const handleEdit = useCallback(async (values: any) => {
    if (!currentRecord) return false;
    const success = await handleEditWithLogic(currentRecord, values, () => {
      updateState({
        editModalVisible: false,
        currentRecord: null,
      });
      handleSearch();
      loadServiceStats();
    });
    return success;
  }, [currentRecord, handleEditWithLogic, updateState, handleSearch, loadServiceStats]);

  // 切换状态
  const handleToggleStatus = useCallback(async (record: any) => {
    const success = await handleToggleStatusWithLogic(record, () => {
      handleSearch();
      loadServiceStats();
    });
  }, [handleToggleStatusWithLogic, handleSearch, loadServiceStats]);

  // 删除微服务
  const handleDelete = useCallback(async (record: any) => {
    const success = await handleDeleteWithLogic(record, () => {
      handleSearch();
      loadServiceStats();
    });
  }, [handleDeleteWithLogic, handleSearch, loadServiceStats]);

  // Token管理功能
  const handleManageToken = useCallback((record: any) => {
    updateState({
      currentRecord: record,
      tokenModalVisible: true,
    });
  }, [updateState]);

  // Token操作完成后的回调
  const handleTokenOperation = useCallback(async (success: boolean) => {
    handleTokenOperationResult(success, handleSearch);
  }, [handleSearch]);

  // Token表单提交处理
  const handleTokenFormSubmit = useCallback(async (values: any): Promise<boolean> => {
    let success = false;
    const isUpdate = !!tokenInfo;

    if (isUpdate) {
      success = await updateToken(values);
    } else {
      success = await createToken(values);
    }

    if (success) {
      // 创建或更新成功后，刷新数据
      handleSearch();
      loadServiceStats();

      // 如果是更新，关闭 modal；如果是创建，保持 modal 打开以显示新创建的 token
      if (isUpdate) {
        updateState({
          tokenModalVisible: false,
          currentRecord: null,
        });
      }
    }

    return success;
  }, [tokenInfo, updateToken, createToken, updateState, handleSearch, loadServiceStats]);

  // 健康检测
  const handleHealthCheck = useCallback(async (record: any) => {
    console.log('[ServiceManagement] 健康检查被触发，服务:', record.name, record);
    await handleHealthCheckWithLogic(record, () => {
      console.log('[ServiceManagement] 健康检查成功，刷新列表');
      handleSearch();
    }, (error) => {
      console.error("[ServiceManagement] 健康检测异常:", error);
    });
  }, [handleHealthCheckWithLogic, handleSearch]);

  // 查看权限
  const handleViewPermissions = useCallback((record: any) => {
    updateState({
      currentRecord: record,
      permissionViewVisible: true,
    });
  }, [updateState]);

  // 初始化加载数据
  useEffect(() => {
    handleSearch();
  }, []);

  // 强制刷新权限数量的方法
  const refreshPermissionCounts = useCallback(async () => {
    if (serviceList.length > 0) {
      console.log('🔄 强制刷新权限数量...');
      try {
        const servicesWithPermissions = await calculateServicePermissions(serviceList);
        setServiceList(servicesWithPermissions);
      } catch (error) {
        console.error('刷新权限数量失败:', error);
      }
    }
  }, [serviceList, calculateServicePermissions]);

  // 当服务列表更新时重新计算权限和统计信息
  useEffect(() => {
    // 清除之前的定时器
    if (permissionCalculationTimeoutRef.current) {
      clearTimeout(permissionCalculationTimeoutRef.current);
    }

    if (serviceList.length > 0) {
      // 检查是否需要计算权限数量（避免重复计算）
      // 只对没有权限数量的服务进行计算（actual_permissions_count 为 undefined 或 null）
      // 注意：如果服务列表刚加载，actual_permissions_count 可能是 undefined，需要计算
      const needsPermissionCalculation = serviceList.some(service => {
        const currentCount = service.actual_permissions_count;
        // 如果为 undefined 或 null，需要计算
        // 如果为 0 且服务有 registered_operations，可能是初始值，也需要重新计算
        if (currentCount === undefined || currentCount === null) {
          return true;
        }
        // 如果为 0 但服务有 registered_operations，说明可能计算失败，需要重新计算
        if (currentCount === 0 && service.registered_operations && service.registered_operations > 0) {
          return true;
        }
        return false;
      });

      console.log(`🔍 检查权限计算需求: needsPermissionCalculation=${needsPermissionCalculation}, serviceList.length=${serviceList.length}`);

      if (needsPermissionCalculation && !permissionCalculationRef.current) {
        console.log('🚀 开始权限计算...');
        // 使用防抖机制，延迟500ms执行权限计算
        permissionCalculationTimeoutRef.current = setTimeout(() => {
          permissionCalculationRef.current = true;
          // 计算权限数量
          calculateServicePermissions(serviceList).then((servicesWithPermissions) => {
            console.log('✅ 权限计算完成，更新服务列表');
            setServiceList(servicesWithPermissions);
            permissionCalculationRef.current = false;
          }).catch(error => {
            console.error('❌ 计算权限数量失败:', error);
            permissionCalculationRef.current = false;
          });
        }, 500);
      } else if (!needsPermissionCalculation) {
        console.log('ℹ️ 所有服务都有权限数量，跳过计算');
      }
    }

    // 立即加载统计信息（这个不会导致大量API调用）
    loadServiceStats();

    // 清理函数
    return () => {
      if (permissionCalculationTimeoutRef.current) {
        clearTimeout(permissionCalculationTimeoutRef.current);
      }
    };
  }, [serviceList.length]); // 只依赖 serviceList.length，避免无限循环

  // 获取Token表单初始值
  const getTokenFormInitialValuesCallback = useCallback(() => {
    return getTokenFormInitialValues(tokenInfo);
  }, [tokenInfo]);

  useEffect(() => {
    if (!tokenFormRef) {
      return;
    }

    const nextValues = getTokenFormInitialValuesCallback();
    console.log("🔍 [useServiceManagementLogic] 更新Token表单值:", {
      tokenInfo,
      nextValues,
      allowed_audience: nextValues.allowed_audience,
    });

    if (typeof tokenFormRef.setFieldsValue === "function") {
      // 使用 setFields 确保 Select 组件正确更新
      const fields = Object.keys(nextValues).map((key) => ({
        name: key,
        value: nextValues[key],
      }));
      tokenFormRef.setFields(fields);
      // 同时也调用 setFieldsValue 作为备用
      tokenFormRef.setFieldsValue(nextValues);
    }
  }, [tokenInfo, tokenFormRef, getTokenFormInitialValuesCallback]);

  // 获取编辑表单初始值
  const getEditFormInitialValuesCallback = useCallback(() => {
    return getEditFormInitialValues(currentRecord);
  }, [currentRecord]);

  return {
    // 状态
    state,
    serviceList,
    loading,
    total,
    submitting,
    tokenSubmitting,
    tokenInfo,
    tokenFormRef,

    // 方法
    handleSearch,
    handleReset,
    handlePaginationChange: handlePaginationChangeCallback,
    handleCreate,
    handleEdit,
    handleToggleStatus,
    handleDelete,
    handleManageToken,
    handleTokenOperation,
    handleTokenFormSubmit,
    handleHealthCheck,
    handleViewPermissions,
    loadServiceStats,
    getTokenFormInitialValues: getTokenFormInitialValuesCallback,
    getEditFormInitialValues: getEditFormInitialValuesCallback,

    // 状态更新方法
    updateState,
    setTokenFormRef,

    // Token 相关方法
    copyToken,
    refreshToken: async () => {
      await refreshToken();
      handleTokenOperation(true);
    },
    deleteToken: async () => {
      await deleteToken();
      handleTokenOperation(true);
    },

    // 暴露底层 serviceList 更新能力，方便页面在拖拽排序后即时刷新列表顺序
    setServiceList,
  };
};
